<template>
  <div>
    <AiChatWidgetChatBubble
      v-if="showChatBubble"
      :key="`bubble-${currentLanguage}-${languageChangeTrigger}`"
      :active-state="bubbleState"
      :client-info="clientInfo"
      @state-change="handleStateChange"
      @first-hover="handleFirstHover"
    />
    <AiChatWidgetSmartDrawer
      :key="`drawer-${currentLanguage}-${languageChangeTrigger}`"
      :is-open="isOpen"
      :active-state="bubbleState"
      :should-load="loadDrawer"
      :client-info="clientInfo"
      @submit="handleSubmitWithChatSessionId"
      @state-change="handleStateChange"
      @close="handleDrawerClose"
      @language-selected="handleLanguageSelected"
    />
  </div>
</template>

<script setup>
import { useStore } from 'vuex';
import { getStreamConversation, deleteSession } from '~/helpers/api/stream';
import { setGlobalChatInstance, useAiChatWidget } from '~/composables/useAiChatWidget';
import { useAiChatClientInfo } from '~/composables/useAiChatClientInfo';

let chat = null;
let isConnecting = false;
let eventListenerAdded = false;

const store = useStore();
const secondaryOrgId = store.state.secondaryOrgId;
const writableApi = useRuntimeConfig().public.WRITABLE_BASE_API_URL;
const { sessionId: sId } = useSession();

const languageChangeTrigger = ref(0);

const {
  bubbleState,
  aiChatEnabled,
  aiContactFormEnabled,
  isOpen,
  loadDrawer,
  handleStateChange,
  handleDrawerClose,
  handleFirstHover,
  handleSubmit,
} = useAiChatWidget();
const { $intl, $ld } = useNuxtApp();
const currentLanguage = computed(() => $intl.locale);
const showChatBubble = computed(() => aiChatEnabled.value || aiContactFormEnabled.value);

const { clientInfo } = useAiChatClientInfo();

const handlePageUnload = () => {
  if (chat && chat.isConnected.value && sId.value) {
    void deleteSession({
      endpoint: writableApi,
      secondaryOrgId,
      sessionId: sId.value,
    });
  }
};

const handleLanguageSelected = async (code) => {
  const language = clientInfo.value.languages.find((language) => language.code === code);
  if (language?.code !== $intl.locale) {
    $intl.$setLocale(language?.code);
    languageChangeTrigger.value++;
  }
};

const canInitializeStreamConnection = computed(() => {
  return import.meta.client && $ld?.flags?.cmsAiChat && !isConnecting;
});

const connectToStream = async () => {
  if (!canInitializeStreamConnection.value) return;

  if (!chat) {
    chat = useChat();
    setGlobalChatInstance(chat);
  }

  if (!chat.isConnected.value) {
    isConnecting = true;
    try {
      const config = {
        endpoint: writableApi,
        secondaryOrgId,
        sessionId: sId.value,
      };

      const welcomeMessage = clientInfo.value.chatConfiguration?.branding?.welcome_message;
      await chat.connectWithProvider(async () => await getStreamConversation(config), config, { welcomeMessage });
    } catch (e) {
      console.error("Can't connect to Stream", e);
    } finally {
      isConnecting = false;
    }
  }
};

const chatWidgetReady = computed(() => {
  return import.meta.client || $ld?.flags?.cmsAiChat;
});

watch(bubbleState, (newState) => {
  if (!chatWidgetReady.value) return;

  if (newState === 'closed') {
    if (eventListenerAdded) {
      window.removeEventListener('pagehide', handlePageUnload);
      eventListenerAdded = false;
    }
  } else {
    if (!eventListenerAdded) {
      window.addEventListener('pagehide', handlePageUnload);
      eventListenerAdded = true;
    }
  }

  if (newState === 'ai-chat') {
    connectToStream();
  }
});

onUnmounted(() => {
  window.removeEventListener('pagehide', handlePageUnload);
});
const handleSubmitWithChatSessionId = (newFormData) => {
  handleSubmit({ ...(newFormData ?? {}), chatSessionId: chat?.channelId?.value });
};
</script>
